import * as Sentry from '@sentry/react';
import { Box, VStack } from '@televet/kibble-ui/build/chakra';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { PetParent } from 'pages/Tasks/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import useDebounce from 'shared/hooks/useDebounce';
import {
  useFindUniqueClinicPetParentSearchResultLazyQuery,
  useSearchPetParentAndPetsLazyQuery,
} from 'shared/types/graphql';
import PetParentSearchResultCard from './PetParentSearchResultCard';

type PetParentSearchProps = {
  selectedPetParentId?: string;
  onSelect: (petParentId: string) => void;
  onDelete?: () => void;
  displayValidationError?: boolean;
};

export const PetParentSearch = ({
  selectedPetParentId,
  onSelect,
  onDelete,
  displayValidationError,
}: PetParentSearchProps): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedPetParent, setSelectedPetParent] = useState<PetParent | undefined>(undefined);

  const { currentClinicId } = useClinicUser();
  const [debouncedSearchTerm] = useDebounce(searchTerm, 350);
  const toast = useToast();

  const [tryFetchingProvidedPetParentData, setTryFetchingProvidedPetParentData] = useState(false);
  const [getPetParentData] = useFindUniqueClinicPetParentSearchResultLazyQuery();

  const [search, { data: searchResults, loading: isLoadingSearchResults }] = useSearchPetParentAndPetsLazyQuery({
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    onError: (e) => {
      toast({
        title: 'Sorry!',
        description: 'We are having trouble looking up this client. Please try again.',
        status: 'error',
      });
      Sentry.captureException(e);
    },
  });

  const groupedPetParents = useMemo(() => {
    if (!searchResults?.clinicPetParentSearch) return [];
    return [...searchResults.clinicPetParentSearch].sort((a, b) => {
      const fullNameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const fullNameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      if (fullNameA < fullNameB) return -1;
      if (fullNameA > fullNameB) return 1;
      return 0;
    });
  }, [searchResults]);

  const onPetParentSelect = useCallback(
    (petParent: PetParent): void => {
      onSelect(petParent.id);
      setSearchTerm('');
      setShowSearchResults(false);
    },
    [onSelect],
  );

  const onPetParentDelete = useCallback((): void => {
    if (onDelete) {
      onDelete();
    }
    setSearchTerm('');
    setShowSearchResults(false);
  }, [onDelete]);

  useEffect(() => {
    // Prevent multiple searches from being triggered at once
    setShowSearchResults(!!debouncedSearchTerm.length);
    if (!debouncedSearchTerm) return;
    search({
      variables: {
        whereInput: {
          and: [
            {
              or: [
                { fullName: debouncedSearchTerm },
                { pimsId: debouncedSearchTerm },
                { petNames: debouncedSearchTerm },
                { petPimsIds: debouncedSearchTerm },
              ],
            },
          ],
        },
        clinicId: currentClinicId || '',
        pageSize: 25,
      },
    });
  }, [currentClinicId, debouncedSearchTerm, search]);

  useEffect(() => {
    if (!selectedPetParentId) {
      return setSelectedPetParent(undefined);
    }
    const foundParent = groupedPetParents.find((parent) => parent.id === selectedPetParentId);
    if (foundParent) {
      return setSelectedPetParent(foundParent);
    }
    //  If the selected pet parent is not found in the current search results,
    // attempt to fetch the pet parent data using the provided ID. and trigger the effect below.
    setTryFetchingProvidedPetParentData(true);
  }, [groupedPetParents, selectedPetParentId, toast]);

  // This effect is used to fetch the pet parent when a provided ID is not found in the current search results.
  // Likely used when editing an existing record where the pet parent was previously selected,
  useEffect(() => {
    if (!tryFetchingProvidedPetParentData) {
      return;
    }
    // If not found in the current search results, fetch the pet parent data
    getPetParentData({
      variables: {
        where: { id: selectedPetParentId },
      },
    })
      .then((response) => {
        const petParent = response.data?.findUniqueClinicPetParent;
        if (petParent) {
          setSelectedPetParent(petParent);
        } else {
          onDelete?.(); // The pet parent no longer exists, so we call onDelete to clear the selection
        }
      })
      .finally(() => {
        setTryFetchingProvidedPetParentData(false);
      });
  }, [getPetParentData, onDelete, selectedPetParentId, tryFetchingProvidedPetParentData]);

  return (
    <VStack pos="relative" w="100%" spacing={4}>
      <TextInput
        label="Client"
        leftElement={<Icon name="magnifyingGlass" />}
        onChange={(e): void => setSearchTerm(e.target.value)}
        value={searchTerm}
        isClearable
        onClear={(): void => setSearchTerm('')}
        isInvalid={displayValidationError}
        errorText="Please select a client or patient"
        data-testid="pet-parent-search-input"
      />
      {showSearchResults && (
        <VStack
          className="PetParentSearchResultsContainer"
          maxH="220px"
          background="background.default"
          overflow="auto"
          w="100%"
          border="1px"
          borderColor="border.default"
          borderRadius="base"
          p={4}
          pos="absolute"
          zIndex="banner"
          shadow="md"
          mt={2}
          top="65px"
          spacing={3}
        >
          {isLoadingSearchResults ? (
            <Spinner />
          ) : !groupedPetParents.length ? (
            <Text>No results found</Text>
          ) : (
            groupedPetParents.map((petParent) => (
              <PetParentSearchResultCard key={petParent.id} petParent={petParent} onSelect={onPetParentSelect} />
            ))
          )}
        </VStack>
      )}
      {!!selectedPetParent && (
        <Box mt={4} width="100%">
          <PetParentSearchResultCard petParent={selectedPetParent} onDelete={onPetParentDelete} />
        </Box>
      )}
    </VStack>
  );
};
